// Team Cup Firebase RTDB layer. All Team Cup data lives under
// `tournaments/{tid}/teamCup/*` so it's namespaced away from the existing
// flight/team/scores tree.
//
// Schema:
//   /tournaments/{tid}/info.teamCupConfig = {
//     scoringSystem: 'official' | 'custom',
//     scatsEnabled, scatsPerHole, cthEnabled, cthPerPar3, handicapsOn,
//   }
//   /tournaments/{tid}/teamCup/teams/{teamId} = { name, playerIds: [pid…] }
//   /tournaments/{tid}/teamCup/groups/{groupId} = { code, name, playerIds }
//   /tournaments/{tid}/teamCup/scores/{groupId}/{hole}/{pid} = gross
//   /tournaments/{tid}/teamCup/cth/{hole}/{groupId} = { v: pid|'none', t }
//   /tournaments/{tid}/teamCup/status = 'open' | 'final'
//
// Player records are reused from the existing /players list — Team Cup
// just stores playerIds[].

import { ref, push, set, update, remove, onValue, get } from 'firebase/database'
import { db } from './firebase.js'

export const DEFAULT_TEAM_CUP_CONFIG = {
  scoringSystem: 'official',
  scatsEnabled: false,
  scatsPerHole: 0,
  cthEnabled: false,
  cthPerPar3: 0,
  handicapsOn: false,
}

// ── Config (stored on tournament info for easy reads with the rest) ───
export async function updateTeamCupConfig(tid, partial) {
  const snap = await get(ref(db, `tournaments/${tid}/info/teamCupConfig`))
  const merged = { ...DEFAULT_TEAM_CUP_CONFIG, ...(snap.val() || {}), ...partial }
  await update(ref(db, `tournaments/${tid}/info`), { teamCupConfig: merged })
  return merged
}

// ── Teams ──────────────────────────────────────────────────────────────
export async function createTeamCupTeam(tid, data) {
  const r = ref(db, `tournaments/${tid}/teamCup/teams`)
  const node = push(r)
  await set(node, { name: data?.name || '', playerIds: data?.playerIds || [] })
  return node.key
}
export async function updateTeamCupTeam(tid, teamId, partial) {
  await update(ref(db, `tournaments/${tid}/teamCup/teams/${teamId}`), partial)
}
export async function deleteTeamCupTeam(tid, teamId) {
  await remove(ref(db, `tournaments/${tid}/teamCup/teams/${teamId}`))
}

// ── Groups (foursomes — same vocabulary as the rest of the app) ────────
export async function createTeamCupGroup(tid, data) {
  const r = ref(db, `tournaments/${tid}/teamCup/groups`)
  const node = push(r)
  await set(node, {
    code: data?.code || randomGroupCode(),
    name: data?.name || '',
    playerIds: data?.playerIds || [],
  })
  return node.key
}
export async function updateTeamCupGroup(tid, groupId, partial) {
  await update(ref(db, `tournaments/${tid}/teamCup/groups/${groupId}`), partial)
}
export async function deleteTeamCupGroup(tid, groupId) {
  await remove(ref(db, `tournaments/${tid}/teamCup/groups/${groupId}`))
  await remove(ref(db, `tournaments/${tid}/teamCup/scores/${groupId}`))
}

// ── Score writes ───────────────────────────────────────────────────────
export async function setTeamCupScore(tid, groupId, hole, pid, gross) {
  const path = `tournaments/${tid}/teamCup/scores/${groupId}/${hole}/${pid}`
  if (gross == null || gross === '' || Number(gross) <= 0) {
    await remove(ref(db, path))
    return
  }
  await set(ref(db, path), Number(gross))
}

// ── CTH writes ────────────────────────────────────────────────────────
export async function setTeamCupCth(tid, hole, groupId, value) {
  // value is a playerId string or 'none'.
  const path = `tournaments/${tid}/teamCup/cth/${hole}/${groupId}`
  await set(ref(db, path), { v: value, t: Date.now() })
}

// ── Status ────────────────────────────────────────────────────────────
export async function setTeamCupStatus(tid, status) {
  await set(ref(db, `tournaments/${tid}/teamCup/status`), status)
}

// ── Watchers ──────────────────────────────────────────────────────────
export function watchTeamCup(tid, cb) {
  return onValue(ref(db, `tournaments/${tid}/teamCup`), (snap) => cb(snap.val() || {}))
}

// ── Helpers ───────────────────────────────────────────────────────────
function randomGroupCode() {
  const letters = 'ABCDEFGHJKMNPQRSTUVWXYZ'
  let out = ''
  for (let i = 0; i < 4; i++) out += letters[Math.floor(Math.random() * letters.length)]
  return out
}
